import React, { useRef, useState } from 'react';
import { Box, ClickAwayListener, Popper, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

interface ModalSelectProps {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  emptyText?: string;
}

/**
 * Single-select dropdown styled to match the AIMDv2 AudienceDropdown (trigger,
 * outline and panel), for use inside dialogs.
 */
const ModalSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  emptyText = 'No options available'
}: ModalSelectProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        sx={{
          width: '100%',
          height: 40,
          px: '12px',
          background: 'rgba(20,20,20,1)',
          borderRadius: '6px',
          outline: open
            ? '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))'
            : '1px solid var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.4))',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          // A phone: a long choice wraps inside the field rather than being cut.
          [MOBILE_SX]: { height: 'auto', minHeight: 40, py: '8px' }
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontFamily: 'Inter',
            color: selected ? 'var(--Text-text-primary, #FDFDFD)' : 'var(--Text-text-tertiary, #868686)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            [MOBILE_SX]: { whiteSpace: 'normal', overflowWrap: 'anywhere' }
          }}
        >
          {selected ? selected.label : placeholder}
        </Typography>
        {open ? (
          <KeyboardArrowUpIcon sx={{ color: 'var(--Stroke-icon-dim, #868686)', fontSize: 20 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: 'var(--Stroke-icon-dim, #868686)', fontSize: 20 }} />
        )}
      </Box>

      {/* Above the Dialog (zIndex 1300) */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1400, width: anchorRef.current?.offsetWidth }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            sx={{
              mt: '4px',
              background: 'rgba(20,20,20,1)',
              borderRadius: '6px',
              outline: '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))',
              maxHeight: 300,
              overflowY: 'auto'
            }}
          >
            {options.length === 0 ? (
              <Typography
                sx={{
                  px: '12px',
                  py: '8px',
                  fontSize: 14,
                  fontFamily: 'Inter',
                  color: 'var(--Text-text-tertiary, #868686)'
                }}
              >
                {emptyText}
              </Typography>
            ) : (
              options.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    sx={{
                      px: '12px',
                      py: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                      '&:hover': { background: 'rgba(255,255,255,0.06)' }
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 1,
                        fontSize: 14,
                        fontFamily: 'Inter',
                        lineHeight: '20px',
                        color: isSelected ? 'var(--Text-text-primary, #FDFDFD)' : 'var(--Text-text-secondary, #B6B6B6)'
                      }}
                    >
                      {opt.label}
                    </Typography>
                    {isSelected && <CheckIcon sx={{ fontSize: 16, color: 'var(--Text-text-primary, #FDFDFD)' }} />}
                  </Box>
                );
              })
            )}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default ModalSelect;
